import { api } from './api';

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation({
      query: ({ email, password }) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation Login($identifier: String!, $password: String!) {
              loginWithEmailOrPhone(identifier: $identifier, password: $password) {
                success
                message
              }
            }
          `,
          variables: { identifier: email, password },
        },
      }),
      transformResponse: (response: any) => response.data,
    }),
    getCurrentUser: builder.query({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query {
              authenticatedItem {
                ... on User {
                  id
                  full_name
                  email
                  phone
                  role {
                    name
                    permissions {
                      key
                    }
                  }
                  profile_picture_file {
                    attachment {
                      url
                    }
                  }
                  account_status {
                    id
                    status
                  }
                }
              }
            }
          `,
        },
      }),
      transformResponse: (response: any) => response.data.authenticatedItem,
    }),
    getCountries: builder.query({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query {
              countries {
                id
                name
              }
            }
          `,
        },
      }),
      transformResponse: (response: any) => response.data.countries,
    }),
    getRegions: builder.query({
      query: (countryId) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetRegions($countryId: ID!) {
              regions(where: { country: { id: { equals: $countryId } } }) {
                id
                name
              }
            }
          `,
          variables: { countryId },
        },
      }),
      transformResponse: (response: any) => response.data?.regions || [],
    }),
    getRoles: builder.query({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query {
              roles {
                id
                name
                type
              }
            }
          `,
        },
      }),
      transformResponse: (response: any) => response.data.roles,
    }),
    endSession: builder.mutation({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation {
              endSession
            }
          `,
        },
      }),
      transformResponse: (response: any) => response.data.endSession,
    }),
    verifyOtp: builder.mutation({
      query: ({ identifier, otp }) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation VerifyOtp($identifier: String!, $otp: String!) {
              verifyOtp(identifier: $identifier, otp: $otp) {
                message
                success
              }
            }
          `,
          variables: { identifier, otp },
        },
      }),
      transformResponse: (response: any) => response.data,
    }),
    resendOtp: builder.mutation({
      query: (identifier) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation ResendOtp($identifier: String!) {
              resendOtp(identifier: $identifier) {
                message
                success
              }
            }
          `,
          variables: { identifier },
        },
      }),
      transformResponse: (response: any) => response.data,
    }),
    passwordResetRequest: builder.mutation({
      query: (identifier: string) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation PasswordResetRequest($identifier: String!) {
              passwordResetRequest(identifier: $identifier) {
                message
                success
              }
            }
          `,
          variables: { identifier },
        },
      }),
      transformResponse: (response: any) => response.data,
    }),
    passwordResetConfirm: builder.mutation({
      query: ({ identifier, otp, newPassword }: { identifier: string; otp: string; newPassword: string }) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation PasswordResetConfirm($identifier: String!, $otp: String!, $newPassword: String!) {
              passwordResetConfirm(identifier: $identifier, otp: $otp, newPassword: $newPassword) {
                message
                success
              }
            }
          `,
          variables: { identifier, otp, newPassword },
        },
      }),
      transformResponse: (response: any) => response.data,
    }),
    // Admin query to get users with role and search filtering (paginated)
    getUsers: builder.query({
      query: ({
        roleType,
        search,
        take = 10,
        skip = 0,
      }: {
        roleType?: string;
        search?: string;
        take?: number;
        skip?: number;
      }) => {
        const whereClause: any = {};
        if (roleType) {
          whereClause.role = { type: { equals: roleType } };
        }
        if (search) {
          whereClause.OR = [
            { full_name: { contains: search, mode: 'insensitive' } },
            { email: { contains: search, mode: 'insensitive' } },
          ];
        }
        return {
          url: '',
          method: 'POST',
          body: {
            query: `
              query GetUsers($where: UserWhereInput!, $take: Int, $skip: Int) {
                users(where: $where, orderBy: { created_at: desc }, take: $take, skip: $skip) {
                  id
                  full_name
                  email
                  phone
                  created_at
                  role {
                    id
                    name
                    type
                  }
                  account_status {
                    id
                    status
                  }
                  profile_picture_file {
                    attachment {
                      url
                    }
                  }
                }
                usersCount(where: $where)
              }
            `,
            variables: { where: whereClause, take, skip },
          },
        };
      },
      transformResponse: (response: any) => ({
        users: response.data.users,
        total: response.data.usersCount,
      }),
      providesTags: ['User'],
    }),

    // Get a single user by ID (admin)
    getUserById: builder.query({
      query: (id: string) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetUserById($id: ID!) {
              user(where: { id: $id }) {
                id
                full_name
                email
                phone
                created_at
                role {
                  id
                  name
                  type
                }
                account_status {
                  id
                  status
                }
                identity_verification_status {
                  id
                  status
                }
                gender
                language
                location {
                  id
                  country { id name }
                  region { id name }
                }
                profile_picture_file {
                  attachment {
                    url
                  }
                }
              }
            }
          `,
          variables: { id },
        },
      }),
      transformResponse: (response: any) => response.data.user,
      providesTags: (result, error, id) => [{ type: 'User', id: id as string }],
    }),

    exportTalents: builder.query<Record<string, unknown>[], void>({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query ExportTalents {
              exportTalents
            }
          `,
        },
      }),
      transformResponse: (response: any) => response.data?.exportTalents || [],
    }),

    // Delete a user (admin)
    deleteUser: builder.mutation({
      query: (id: string) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation DeleteUser($id: ID!) {
              deleteUser(where: { id: $id }) {
                id
              }
            }
          `,
          variables: { id },
        },
      }),
      transformResponse: (response: any) => response.data.deleteUser,
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useLoginMutation,
  useGetRolesQuery,
  useGetCountriesQuery,
  useGetRegionsQuery,
  useLazyGetRegionsQuery,
  useVerifyOtpMutation,
  useResendOtpMutation,
  usePasswordResetRequestMutation,
  usePasswordResetConfirmMutation,
  useGetUsersQuery,
  useGetUserByIdQuery,
  useLazyExportTalentsQuery,
} = authApi;
